import type { GeneratedNodeAttribute } from "@keydials/api-client";

/**
 * Authored attribute groups (KEY-436) — the heading an author puts a run of fields under, drawn in the
 * user app as one bordered, collapsible card. Moved here from web-admin (KEY-560) so the user app's builder
 * groups fields by the same rules; web-admin re-exports it from its old path.
 *
 * Deliberately NOT in web-admin's `attributeViews.ts`, whose `AttributeGroup` is a different idea wearing
 * the same word: that one bands the editor's own list by target or type at read time and is never stored.
 * This one is authored, saved, and shown to the people filling the dial in. Nor is it the workspace's
 * `attributeFieldsets.tsx`, which DRAWS these groups; this module only edits them.
 *
 * The title is the whole identity — there is no key and no record — so everything here works on string
 * equality, and a group exists exactly as long as some field names it.
 */

/** The group titles already in use at a level, in first-appearance order, for the editor's datalist. */
export function fieldsetTitles(attributes: readonly GeneratedNodeAttribute[], target: string): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const a of attributes) {
    const title = a.fieldsetTitle?.trim();
    if (!title || a.target !== target || seen.has(title)) continue;
    seen.add(title);
    out.push(title);
  }
  return out;
}

/**
 * The members of one group, as indices into the caller's array — never copies, for the same reason
 * web-admin's `attributeViews.ts` gives: every mutation in the admin editor is index-addressed, and two
 * mid-authoring rows can share a blank key, so an index is the only stable way to point at a card.
 */
export function fieldsetMembers(
  attributes: readonly GeneratedNodeAttribute[],
  target: string,
  title: string,
): number[] {
  const wanted = title.trim();
  return attributes
    .map((a, i) => ({ a, i }))
    .filter(({ a }) => a.target === target && (a.fieldsetTitle?.trim() ?? "") === wanted)
    .map(({ i }) => i);
}

/**
 * Whether a group's members sit in one unbroken run at their level.
 *
 * Worth telling the author about, because the user app draws the card where the FIRST member sits and
 * pulls the rest in — so a group split around an ungrouped field renders correctly but moves that field
 * below the card, which reads as the editor having reordered something on its own.
 *
 * Judged among the fields at the same target only: another level's fields are drawn on another surface
 * entirely, so they cannot interrupt anything.
 */
export function fieldsetIsContiguous(
  attributes: readonly GeneratedNodeAttribute[],
  target: string,
  title: string,
): boolean {
  const atTarget = attributes.filter((a) => a.target === target);
  const positions = atTarget
    .map((a, i) => ({ a, i }))
    .filter(({ a }) => (a.fieldsetTitle?.trim() ?? "") === title.trim())
    .map(({ i }) => i);
  if (positions.length < 2) return true;
  return positions[positions.length - 1] - positions[0] === positions.length - 1;
}

/**
 * Renames a group everywhere it is used at one level — an EXPLICIT action, never a side effect of
 * typing in a field's Group box.
 *
 * KEY-551: this used to be wired to the box's `onChange`, so every keystroke rewrote every member and a
 * field already in a group could not be moved to a different one — the whole group followed it. Assigning
 * one field to a group is the common action and is now plain per-field editing (`set(i, {fieldsetTitle})`);
 * renaming the group as a unit is this, behind its own control.
 *
 * Scoped to one level, because that is the boundary a card is drawn within: two levels sharing a title
 * were always going to render as two cards, so renaming one shouldn't drag the other along.
 */
export function renameFieldset(
  attributes: readonly GeneratedNodeAttribute[],
  target: string,
  from: string,
  to: string,
): GeneratedNodeAttribute[] {
  const was = from.trim();
  const now = to.trim();
  if (was.length === 0) return [...attributes];
  const next = now.length > 0 ? now : null;
  return attributes.map((a) =>
    a.target === target && (a.fieldsetTitle?.trim() ?? "") === was ? { ...a, fieldsetTitle: next } : a);
}
